import { useEffect, useMemo, useRef, useState } from 'react'
import { SegBar } from '../components/SegBar'
import { IconBtn } from '../components/IconBtn'
import { makeTimerSession, type TimerSession } from '../logic/timers'
import type { Exercise, LevelMeta, RuleKey } from '../logic/types'
import type { SessionQueue } from '../logic/session'

const ADVANCE_MS = 800  // correct answer → auto-advance
const HINT_DELAY_MS = 500  // wrong answer → sheet slides up after this

interface Props {
  level: LevelMeta
  session: SessionQueue
  rules: Record<RuleKey, { title: string; ru: string }>
  onAnswer: (exerciseId: string, correct: boolean) => void
  onAdvance: (result: 'good' | 'bad') => void
  onExit: () => void
}

type AnswerKey = 'hebben' | 'zijn'

export function Session({ level, session, rules, onAnswer, onAdvance, onExit }: Props) {
  const ex: Exercise | undefined = session.queue[session.queueIndex]
  const [picked, setPicked] = useState<AnswerKey | null>(null)
  const [showHint, setShowHint] = useState(false)
  const [showTranslation, setShowTranslation] = useState(false)
  const timersRef = useRef<TimerSession>(makeTimerSession())

  // Randomized button order per question, memoized by exercise id
  const order = useMemo<AnswerKey[]>(() => {
    if (!ex) return ['hebben', 'zijn']
    return Math.random() < 0.5 ? ['hebben', 'zijn'] : ['zijn', 'hebben']
  }, [ex?.id])

  // Reset on each new question; cancel pending timers on swap.
  useEffect(() => {
    setPicked(null)
    setShowHint(false)
    setShowTranslation(false)
    timersRef.current.clearAll()
    return () => {
      timersRef.current.clearAll()
    }
  }, [ex?.id])

  // Hard-cancel on unmount (exit/advance/result)
  useEffect(() => {
    return () => timersRef.current.clearAll()
  }, [])

  if (!ex) return null

  const rule = rules[ex.rule]
  const correctForm = ex.forms[ex.answer]
  const locked = picked !== null

  const handleAnswer = (aux: AnswerKey) => {
    if (locked) return
    setPicked(aux)
    const isCorrect = aux === ex.answer

    onAnswer(ex.id, isCorrect)

    if (isCorrect) {
      timersRef.current.later(() => onAdvance('good'), ADVANCE_MS)
    } else {
      timersRef.current.later(() => setShowHint(true), HINT_DELAY_MS)
    }
  }

  const handleVerder = () => {
    timersRef.current.clearAll()
    setShowHint(false)
    onAdvance('bad')
  }

  const goodSet = useMemo(() => new Set(session.good), [session.good])
  const badSet = useMemo(() => new Set(session.bad), [session.bad])

  return (
    <>
      <section className="screen active" id="scr-session">
        <div className="sess-top">
          <IconBtn
            label="Выйти"
            onClick={() => {
              timersRef.current.clearAll()
              onExit()
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden>
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </IconBtn>
          <div className="sess-info">
            <div className="sess-level">{level.name} · {level.cefr}</div>
            <div className="sess-count">{session.queueIndex + 1} / {session.queue.length}</div>
          </div>
          <div style={{ width: 42, flex: 'none' }} />
        </div>
        <SegBar
          total={session.queue.length}
          current={session.queueIndex}
          goodSet={goodSet}
          badSet={badSet}
          idsInOrder={session.queue.map((e) => e.id)}
        />
        <div className="q-area">
          <div className="q-label">Vul in: hebben of zijn</div>
          <div className="sentence" lang="nl">
            {renderSentence(ex.text, picked, correctForm)}
          </div>
          {ex.translationRu && (
            <>
              <button
                className="translation-toggle"
                aria-expanded={showTranslation}
                aria-controls={`tr-${ex.id}`}
                onClick={() => setShowTranslation((v) => !v)}
              >
                {showTranslation ? 'Скрыть перевод' : 'Посмотреть перевод'}
              </button>
              {showTranslation && (
                <div id={`tr-${ex.id}`} className="translation" lang="ru">
                  {ex.translationRu}
                </div>
              )}
            </>
          )}
        </div>
        <div className="answers">
          {order.map((aux) => {
            const form = ex.forms[aux]
            const cls = buttonClass(aux, ex.answer, picked)
            return (
              <button
                key={aux}
                className={cls}
                onClick={() => handleAnswer(aux)}
                disabled={locked}
              >
                {form}
              </button>
            )
          })}
        </div>
      </section>

      <div className={`backdrop${showHint ? ' open' : ''}`} onClick={handleVerder} aria-hidden />
      <div className={`sheet${showHint ? ' open' : ''}`} role="dialog" aria-label="Подсказка">
        <div className="grip" />
        <div className="hint-kind">Niet juist</div>
        <div className="hint-ans">
          Juist: <b>{correctForm}</b>{' '}
          <span style={{ color: 'var(--ink-soft)', fontSize: 15 }}>({ex.answer})</span>
        </div>
        <div className="hint-rule">
          <div className="rt">{rule.title}</div>
          <div>{rule.ru}</div>
        </div>
        <button className="btn primary" onClick={handleVerder}>Verder</button>
      </div>
    </>
  )
}

function buttonClass(aux: AnswerKey, correct: AnswerKey, picked: AnswerKey | null): string {
  if (picked === null) return 'ans-btn'
  if (aux === correct) return 'ans-btn good'
  if (aux === picked) return 'ans-btn bad'
  return 'ans-btn'
}

function renderSentence(text: string, picked: AnswerKey | null, correctForm: string) {
  const parts = text.split('___')
  const before = parts[0] ?? ''
  const after = parts[1] ?? ''
  const cls = picked === null ? 'slot' : 'slot filled-good'
  const content = picked === null ? '…' : correctForm
  return (
    <>
      {before}
      <span className={cls}>{content}</span>
      {after}
    </>
  )
}
